import {
	confirmAccountDeletion,
	createUser,
	deleteProfilePicture,
	deleteUser,
	fetchAdminUserDetails,
	fetchUserProfile,
	fetchUsers,
	fetchUsersCount,
	requestAccountDeletion,
	updateUser,
	updateUserProfile,
	uploadProfilePicture,
} from "@bodhistreams/api/src/userApi";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { authQueryKeys, useAuthMe } from "./useAuthQueries";

/**
 * Query keys factory for user queries
 */
export const userQueryKeys = {
	all: ["users"],
	profile: () => [...userQueryKeys.all, "profile"],
	lists: () => [...userQueryKeys.all, "list"],
	list: (params) => [...userQueryKeys.lists(), params],
	count: () => [...userQueryKeys.all, "count"],
	detail: (id) => [...userQueryKeys.all, "detail", id],
};

/**
 * Full user profile query — hits the DB (memberships, stripe, etc.).
 * Use useAuthMe() for lightweight auth identity checks.
 * Only fetches when the user is authenticated (useAuthMe returns data).
 */
export const useUserProfile = () => {
	const { data: authUser } = useAuthMe();

	return useQuery({
		queryKey: userQueryKeys.profile(),
		queryFn: fetchUserProfile,
		enabled: !!authUser,
		staleTime: 5 * 60 * 1000,
		gcTime: 30 * 60 * 1000,
		retry: (failureCount, error) => {
			if (error?.response?.status === 401 || error?.response?.status === 403) {
				return false;
			}
			return failureCount < 3;
		},
	});
};

/**
 * Hook to fetch all users (admin only)
 * @param {boolean} enabled - Whether to enable the query
 * @returns {Object} React Query result
 */
export const useUsers = (enabled = true) => {
	return useQuery({
		queryKey: userQueryKeys.lists(),
		queryFn: async () => {
			return await fetchUsers();
		},
		enabled,
		staleTime: 2 * 60 * 1000, // 2 minutes
		gcTime: 10 * 60 * 1000, // 10 minutes
	});
};

/**
 * Hook to fetch users count (admin only)
 * @param {boolean} enabled - Whether to enable the query
 * @returns {Object} React Query result
 */
export const useUsersCount = (enabled = true) => {
	return useQuery({
		queryKey: userQueryKeys.count(),
		queryFn: async () => {
			return await fetchUsersCount();
		},
		enabled,
		staleTime: 5 * 60 * 1000, // 5 minutes
		gcTime: 30 * 60 * 1000, // 30 minutes
	});
};

export const useAdminUserDetails = (userId, enabled = true) => {
	return useQuery({
		queryKey: userQueryKeys.detail(userId),
		queryFn: () => fetchAdminUserDetails(userId),
		enabled: enabled && !!userId,
		staleTime: 30 * 1000,
	});
};

/**
 * Hook to update current user profile
 * @returns {Object} React Query mutation result
 */
export const useUpdateUserProfile = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: updateUserProfile,
		onSuccess: (updatedUser) => {
			// Update the profile cache
			queryClient.setQueryData(userQueryKeys.profile(), updatedUser);
			// Also refresh auth identity in case name/email changed
			queryClient.invalidateQueries({ queryKey: authQueryKeys.me() });
		},
	});
};

/**
 * Hook to create a new user (admin only)
 * @returns {Object} React Query mutation result
 */
export const useCreateUser = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: createUser,
		onSuccess: () => {
			// Invalidate and refetch users list
			queryClient.invalidateQueries({
				queryKey: userQueryKeys.lists(),
			});
			// Invalidate users count
			queryClient.invalidateQueries({
				queryKey: userQueryKeys.count(),
			});
		},
	});
};

/**
 * Hook to update a user (admin only)
 * @returns {Object} React Query mutation result
 */
export const useUpdateUser = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({ userId, userData }) => updateUser(userId, userData),
		onSuccess: (_, { userId }) => {
			queryClient.invalidateQueries({
				queryKey: userQueryKeys.detail(userId),
			});
			// Invalidate users list to reflect changes
			queryClient.invalidateQueries({
				queryKey: userQueryKeys.lists(),
			});
		},
	});
};

/**
 * Hook to delete a user (admin only)
 * @returns {Object} React Query mutation result
 */
export const useDeleteUser = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: deleteUser,
		onSuccess: (_, userId) => {
			// Remove from cache
			queryClient.removeQueries({
				queryKey: userQueryKeys.detail(userId),
			});
			// Invalidate users list
			queryClient.invalidateQueries({
				queryKey: userQueryKeys.lists(),
			});
			// Invalidate users count
			queryClient.invalidateQueries({
				queryKey: userQueryKeys.count(),
			});
		},
	});
};

/**
 * Hook to get the query client for manual cache operations
 * @returns {Object} React Query client
 */
export const useUserQueryClient = () => {
	return useQueryClient();
};

/**
 * Hook to invalidate user queries
 * @returns {Object} Invalidation functions
 */
export const useInvalidateUsers = () => {
	const queryClient = useQueryClient();

	return {
		invalidateAll: () =>
			queryClient.invalidateQueries({
				queryKey: userQueryKeys.all,
			}),
		invalidateProfile: () => {
			queryClient.invalidateQueries({
				queryKey: userQueryKeys.profile(),
			});
		},
		invalidateUsersList: () => {
			queryClient.invalidateQueries({
				queryKey: userQueryKeys.lists(),
			});
		},
		invalidateUsersCount: () => {
			queryClient.invalidateQueries({
				queryKey: userQueryKeys.count(),
			});
		},
		invalidateUser: (userId) => {
			queryClient.invalidateQueries({
				queryKey: userQueryKeys.detail(userId),
			});
		},
	};
};

/**
 * Hook to check if user has free membership
 * Derives from lightweight auth identity (JWT claims).
 * @returns {boolean} True if user has free membership
 */
export const useIsFreeMembership = () => {
	const { data: authUser } = useAuthMe();
	const membershipName = authUser?.membership_name;
	return !membershipName || membershipName.toLowerCase() === "free";
};

/**
 * Hook to get membership info
 * Derives basic name/id from auth identity (JWT claims).
 * For price/device limits/stream minutes, use useUserProfile().
 * @returns {Object} Membership information
 */
export const useMembershipInfo = () => {
	const { data: authUser } = useAuthMe();
	const { data: profile } = useUserProfile();
	return {
		membership: {
			name: authUser?.membership_name,
			id: authUser?.membership_id,
			price: profile?.membership_price,
			max_simultaneous_devices: profile?.membership_max_simultaneous_devices,
		},
		monthlyStreamMinutes: profile?.monthly_stream_minutes || 0,
		monthlyStreamHours: (profile?.monthly_stream_minutes || 0) / 60,
	};
};

/**
 * Hook to upload a profile picture
 * @returns {Object} React Query mutation result
 */
export const useUploadProfilePicture = () => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: uploadProfilePicture,
		onSuccess: (data) => {
			queryClient.setQueryData(userQueryKeys.profile(), (old) =>
				old ? { ...old, profile_picture_url: data.profile_picture_url } : old,
			);
		},
	});
};

/**
 * Hook to delete the profile picture
 * @returns {Object} React Query mutation result
 */
export const useDeleteProfilePicture = () => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: deleteProfilePicture,
		onSuccess: () => {
			queryClient.setQueryData(userQueryKeys.profile(), (old) =>
				old ? { ...old, profile_picture_url: null } : old,
			);
		},
	});
};

/**
 * Hook to request account deletion (emails a confirmation link — the
 * account is only deleted after the user confirms via that link)
 * @returns {Object} React Query mutation result
 */
export const useRequestAccountDeletion = () => {
	return useMutation({
		mutationFn: requestAccountDeletion,
	});
};

/**
 * Hook to confirm account deletion with the token from the deletion email
 * @returns {Object} React Query mutation result
 */
export const useConfirmAccountDeletion = () => {
	return useMutation({
		mutationFn: confirmAccountDeletion,
	});
};
